import { useRouter } from 'next/router';

// URL-backed lens state for the insights pages — which tab's page this is:
//   • growth   — writing SCORES over time (the original dashboard)
//   • gaps     — TEKS standards mastery ("where gaps sit")
//   • students — the same skill gaps, one row per student (latest verdict per
//                skill), opening into a student's every lesson
// Each tab is its own page, opened by a link from the tab band
// (`insightsTabHrefs`), so the lens is only read here, never switched in
// place. Drilling carries it via `querySuffix()` (which forwards `?lens=gaps`).
//
// The Gaps lens needs the viewer's gaps flag, and the Students (Skill gaps)
// lens needs `skill_gaps_v2` on top of it (both resolved by
// `useInsightsAccess`). A lens the viewer lacks is ignored and falls back to
// growth, so a shared link can't surface it.
export type InsightsLens = 'growth' | 'gaps' | 'students';

// The lenses behind the gaps flag — the ones a link may carry only when the
// viewer holds it. One list, read by useLens and by useTimePeriod's link suffix.
export const GAPS_LENSES: readonly InsightsLens[] = ['gaps', 'students'];

export const isGapsLens = (v: unknown): v is 'gaps' | 'students' =>
  typeof v === 'string' && (GAPS_LENSES as readonly string[]).includes(v);

// Whether this viewer may land on a requested gaps lens.
export const isAllowedGapsLens = (
  v: unknown,
  access: { gapsLensOn: boolean; skillGapsOn: boolean },
): v is 'gaps' | 'students' =>
  isGapsLens(v) && (v === 'students' ? access.skillGapsOn : access.gapsLensOn);

export function useLens(
  gapsLensOn: boolean,
  skillGapsOn: boolean,
): {
  lens: InsightsLens;
} {
  const router = useRouter();
  const requested = router.query.lens;
  const lens: InsightsLens = isAllowedGapsLens(requested, { gapsLensOn, skillGapsOn })
    ? requested
    : 'growth';
  return { lens };
}
